/* eslint-disable @typescript-eslint/no-magic-numbers */
import { injectable } from 'inversify';
import type {
  AudioStreamStats,
  VideoStreamStats,
  ScoresCalculatorConfig,
  BaseCalculatorConfig,
  DeviceType,
} from './types';
import { clamp, prepareScore } from './helpers';
import { MOS_MIN_VAL } from './constants';

const DEFAULT_PACKETIZATION_DELAY = 20;
const IE_DTX_VALUE = 8;
const IE_DEFAULT_VALUE = 6;
const IE_BITRATE_BASE = 55;
const IE_MAX_VALUE = 30;
const UNCONSTRAINED_ENCODER_BASE_FLOOR = 4.0;

// QP thresholds below which the encoder produces good quality
// (low bitrate is content-driven, not bandwidth-limited)
const QP_GOOD_QUALITY_THRESHOLDS: Record<string, number> = {
  vp8: 40,
  vp9: 40,
  h264: 25,
};
const DEFAULT_QP_THRESHOLD = 40;

const QAV_COEFFICIENTS: Record<DeviceType, [m1: number, m2: number, m3: number, m4: number]> = {
  mobile: [0.1558, 1.0348, -0.0181, -0.4048],
  pc: [0.1526, 0.8788, 0.0086, -0.2242],
  tv: [0.1496, 1.0267, -0.0248, -0.3477],
};

const BASE_CONFIG: BaseCalculatorConfig = {
  video: {
    codecFactors: {
      vp9: 1.2,
      vp8: 1.0,
      h264: 1.0,
    },
  },
  defaults: {
    audio: {
      ssrc: -1,
      packetLoss: 0,
      bufferDelay: 50,
      roundTripTime: 50,
      fec: true,
      bitrate: 0,
      dtx: false,
    },
    video: {
      ssrc: -1,
      packetLoss: 0,
      bufferDelay: 0,
      roundTripTime: 50,
      codec: 'vp8',
      width: 640,
      height: 480,
      expectedWidth: 640,
      expectedHeight: 480,
      frameRate: 20,
      expectedFrameRate: 20,
      bitrate: 0,
    },
  },
};

@injectable()
export class ScoresCalculator {
  private readonly config: Required<ScoresCalculatorConfig>;

  constructor(config?: ScoresCalculatorConfig) {
    this.config = this.mergeConfig(config);
  }

  calculateAudioMOS(stats: Partial<AudioStreamStats>): number {
    const audio = this.normalizeAudioStats(stats);
    const delay = DEFAULT_PACKETIZATION_DELAY + audio.bufferDelay + audio.roundTripTime / 2;
    const pl = audio.packetLoss;
    const R0 = 100;
    let Ie = 0;

    if (audio.dtx) {
      Ie = IE_DTX_VALUE;
    } else if (audio.bitrate > 0) {
      Ie = clamp(IE_BITRATE_BASE - 4.6 * Math.log(audio.bitrate), 0, IE_MAX_VALUE);
    } else {
      Ie = IE_DEFAULT_VALUE;
    }

    const Bpl = audio.fec ? 20 : 10;
    const Ipl = Ie + ((100 - Ie) * pl) / (pl + Bpl);
    const Id = delay * 0.03 + (delay > 150 ? 0.1 * (delay - 150) : 0);
    const R = clamp(R0 - Ipl - Id, 0, 100);
    const MOS = 1 + 0.035 * R + (R * (R - 60) * (100 - R) * 7) / 1_000_000;

    return prepareScore(MOS);
  }

  calculateVideoMOS(stats: Partial<VideoStreamStats>): number {
    const video = this.normalizeVideoStats(stats);
    const pixels = video.expectedWidth * video.expectedHeight;

    if (!video.frameRate || !pixels || video.bitrate === undefined) {
      return MOS_MIN_VAL;
    }

    const codecFactor = video.codec ? this.config.video?.codecFactors?.[video.codec.toLowerCase()] ?? 1.0 : 1.0;
    const delay = video.bufferDelay + video.roundTripTime / 2;
    const bPPPF = (codecFactor * video.bitrate) / pixels / video.frameRate;
    const bPPPFBase = clamp(0.56 * Math.log(bPPPF) + 5.36, 1, 5);
    const isEncoderUnconstrained = video.decreaseOutboundPenalty === true && video.qualityLimitationReason === 'none';
    const qpThreshold = QP_GOOD_QUALITY_THRESHOLDS[video.codec?.toLowerCase() ?? ''] ?? DEFAULT_QP_THRESHOLD;
    const isLowQP = video.averageQP !== undefined && video.averageQP < qpThreshold;
    const shouldFloorBitratePenalty = isEncoderUnconstrained || video.isStaticContent === true || isLowQP;
    const base = shouldFloorBitratePenalty
      ? Math.max(bPPPFBase, UNCONSTRAINED_ENCODER_BASE_FLOOR)
      : bPPPFBase;
    const frameRatePenalty =
      video.expectedFrameRate > 0
        ? 1.9 * Math.log(video.expectedFrameRate / video.frameRate)
        : 0;

    const delayPenalty = delay * 0.002;
    const MOS = base - frameRatePenalty - delayPenalty;

    return prepareScore(MOS);
  }

  calculateAVCompositeMOS(Qa: number, Qv: number, deviceType?: DeviceType): number {
    const [m1, m2, m3, m4] = QAV_COEFFICIENTS[deviceType ?? 'pc'];
    const Qav = m1 * Qa + m2 * Qv + m3 * Qa * Qv + m4;

    return prepareScore(Qav);
  }

  private mergeConfig(userConfig?: ScoresCalculatorConfig): Required<ScoresCalculatorConfig> {
    if (!userConfig) {
      return BASE_CONFIG;
    }

    return {
      video: {
        codecFactors: {
          ...BASE_CONFIG.video.codecFactors,
          ...userConfig.video?.codecFactors,
        },
      },
    };
  }

  private normalizeAudioStats(stats: Partial<AudioStreamStats>): AudioStreamStats {
    return {
      ...BASE_CONFIG.defaults.audio,
      ...stats,
    } satisfies AudioStreamStats;
  }

  private normalizeVideoStats(stats: Partial<VideoStreamStats>): VideoStreamStats {
    const normalized = {
      ...BASE_CONFIG.defaults.video,
      ...stats,
    } satisfies VideoStreamStats;

    if (stats.width !== undefined && stats.expectedWidth === undefined) {
      normalized.expectedWidth = stats.width;
    }

    if (stats.height !== undefined && stats.expectedHeight === undefined) {
      normalized.expectedHeight = stats.height;
    }

    if (stats.frameRate !== undefined && stats.expectedFrameRate === undefined) {
      normalized.expectedFrameRate = stats.frameRate;
    }

    return normalized;
  }
}
